
import { supabase } from '@/integrations/supabase/client';
import { Player } from '@/types/player';
import { transformPlayerData } from '@/utils/playerTransform';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type PlayerInsert = Database['public']['Tables']['players']['Insert'];
type PlayerUpdate = Database['public']['Tables']['players']['Update'];

interface UserProfile {
  id: string;
  role: string;
  club_id?: string;
}

export const fetchPlayersData = async (profile: UserProfile) => {
  let query = supabase
    .from('players')
    .select(`
      *,
      profile:profiles(first_name, last_name, email)
    `);

  // Apply role-based filtering with new club structure
  if (profile.role === 'coach') {
    // Coaches see players from teams in their club
    // First get the team IDs for the coach's club
    const { data: clubTeams } = await supabase
      .from('teams')
      .select('id')
      .eq('club_id', profile.club_id);
    
    if (clubTeams && clubTeams.length > 0) {
      const teamIds = clubTeams.map(team => team.id);
      query = query.in('team_id', teamIds);
    } else {
      // No teams in coach's club, return empty
      return [];
    }
  } else if (profile.role === 'player') {
    query = query.eq('profile_id', profile.id);
  } else if (profile.role === 'parent') {
    // For parents, we need to get their children's player data
    const { data: relationships } = await supabase
      .from('parent_child_relationships')
      .select('child_id')
      .eq('parent_id', profile.id);
    
    if (relationships && relationships.length > 0) {
      const childIds = relationships.map(r => r.child_id);
      query = query.in('profile_id', childIds);
    } else {
      return [];
    }
  }
  // Administrators can see all players (no additional filter needed)

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching players:', error);
    toast.error('Failed to load players');
    throw error;
  }

  // Transform the data to match our Player interface
  return (data || []).map(transformPlayerData);
};

export const addPlayerData = async (playerData: Omit<Player, 'id' | 'profile'>) => {
  // Convert Video objects to plain objects for database storage
  const dbPlayerData: Record<string, unknown> = {
    ...playerData,
    video_urls: playerData.video_urls?.map(video => {
      if (typeof video === 'string') {
        // Handle legacy string format
        return video;
      }
      // Handle Video object format
      return {
        id: video.id,
        url: video.url,
        source: video.source,
        title: video.title,
        description: video.description,
        created_at: video.created_at
      };
    }) || []
  };

  const { data, error } = await supabase
    .from('players')
    .insert([dbPlayerData as PlayerInsert])
    .select(`
      *,
      profile:profiles(first_name, last_name, email)
    `)
    .single();

  if (error) {
    console.error('Error adding player:', error);
    toast.error('Failed to add player');
    throw error;
  }

  return transformPlayerData(data);
};

export const updatePlayerData = async (playerId: string, updates: Partial<Player>) => {
  // Convert Video objects to plain objects for database storage
  const dbUpdates = {
    ...updates,
    ...(updates.video_urls && {
      video_urls: updates.video_urls.map(video => {
        if (typeof video === 'string') {
          // Handle legacy string format
          return video;
        }
        // Handle Video object format
        return {
          id: video.id,
          url: video.url,
          source: video.source,
          title: video.title,
          description: video.description,
          created_at: video.created_at
        };
      })
    })
  };

  const { data, error } = await supabase
    .from('players')
    .update(dbUpdates as PlayerUpdate)
    .eq('id', playerId)
    .select(`
      *,
      profile:profiles(first_name, last_name, email)
    `)
    .single();

  if (error) {
    console.error('Error updating player:', error);
    toast.error('Failed to update player');
    throw error;
  }

  return transformPlayerData(data);
};
